// P1 provider authorisation-server contracts (split out of `phase-p1.ts` to stay
// under the file-length budget).
//
// - P1.7 AS metadata: the customer Keycloak realm `.well-known` endpoints + the
//   FAPI-BR v2 provider checks the directory software-statement registration
//   (FASE 2/3) depends on.
// - P1.8 DCR proof: a real dynamic-client-registration exchange against the
//   customer AS over mTLS (FASE 8), with the SSA / registration access token /
//   client secret reduced to presence booleans so no secret reaches evidence.

import { Schema } from 'effect';

import { HttpUrl } from '../../_shared/formats.ts';
import { onboardingKcRealmSchema } from '../phase-p1.ts';
import { ClientId, CustomerId, OrganisationId, SoftwareId } from '../vocabulary/ids/branded-ids.ts';

// P1.7 — authorisation-server metadata (`.well-known/openid-configuration`).
export const onboardingAsMetadataInputSchema = Schema.Struct({
  customerId: CustomerId,
  realm: onboardingKcRealmSchema,
}).annotate({
  title: 'Power onboarding AS metadata input',
  description:
    'Internal Power onboarding payload for fetching the customer authorisation-server .well-known metadata.',
});
export type OnboardingAsMetadataInput = typeof onboardingAsMetadataInputSchema.Type;

const asMetadataFapiChecksSchema = Schema.Struct({
  parEndpointPresent: Schema.Boolean,
  registrationEndpointPresent: Schema.Boolean,
  ps256TokenAuthSupported: Schema.Boolean,
  ps256RequestObjectSupported: Schema.Boolean,
  tlsClientCertBoundAccessTokens: Schema.Boolean,
});

export const onboardingAsMetadataResponseSchema = Schema.Struct({
  issuer: Schema.NonEmptyString,
  authorizationEndpoint: Schema.NonEmptyString,
  tokenEndpoint: Schema.NonEmptyString,
  pushedAuthorizationRequestEndpoint: Schema.NullOr(Schema.NonEmptyString),
  jwksUri: Schema.NonEmptyString,
  registrationEndpoint: Schema.NullOr(Schema.NonEmptyString),
  // Registered client redirect URIs captured in the directory-registration step;
  // empty until that registration is captured for the realm's ecosystem.
  redirectUris: Schema.Array(HttpUrl),
  // Directory software statement binding (org/software ids); null until the
  // per-ecosystem directory registration has been captured.
  softwareStatement: Schema.NullOr(
    Schema.Struct({
      organisationId: OrganisationId,
      softwareId: SoftwareId,
    }),
  ),
  fapiChecks: asMetadataFapiChecksSchema,
  allRequiredPresent: Schema.Boolean,
}).annotate({
  title: 'Power onboarding AS metadata response',
  description:
    'Internal Power onboarding response with the customer authorisation-server endpoints and FAPI-BR v2 checks.',
});
export type OnboardingAsMetadataResponse = typeof onboardingAsMetadataResponseSchema.Type;

// P1.8 — provider DCR proof. Registers a throwaway client against the customer
// AS over mTLS and (by default) deletes it again via RFC 7592.
export const onboardingDcrProbeInputSchema = Schema.Struct({
  customerId: CustomerId,
  realm: onboardingKcRealmSchema,
  // RFC 7592 cleanup of the throwaway client; defaults to on when omitted.
  cleanup: Schema.optionalKey(Schema.Boolean),
}).annotate({
  title: 'Power onboarding DCR probe input',
  description:
    'Internal Power onboarding payload for proving dynamic client registration against the customer authorisation server.',
});
export type OnboardingDcrProbeInput = typeof onboardingDcrProbeInputSchema.Type;

// Deliberately carries NO software statement, registration access token, or
// client secret — only presence booleans — so the persisted evidence proves the
// DCR exchange happened without leaking any credential material.
export const onboardingDcrProbeResponseSchema = Schema.Struct({
  registered: Schema.Boolean,
  httpStatus: Schema.Int,
  clientId: Schema.NullOr(ClientId),
  certBound: Schema.Boolean,
  softwareStatementSent: Schema.Boolean,
  registrationAccessTokenReceived: Schema.Boolean,
  clientSecretIssued: Schema.Boolean,
  cleanup: Schema.Struct({
    attempted: Schema.Boolean,
    succeeded: Schema.Boolean,
  }),
}).annotate({
  title: 'Power onboarding DCR probe response',
  description:
    'Internal Power onboarding response proving a dynamic client registration round-trip (no secrets).',
});
export type OnboardingDcrProbeResponse = typeof onboardingDcrProbeResponseSchema.Type;
